import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getMarketingLinksRequest: ["payload"], // payload: { params }
  getMarketingLinksSuccess: ["payload"], // payload: { data }

  getTotalUsersRequest: ["payload"], // payload: { params }
  getTotalUsersSuccess: ["payload"], // payload: { data }

  getSourceRequest: ["payload"], // payload: { params }
  getSourceSuccess: ["payload"], // payload: { data }
  getSourceFailure: null,

  getCampaignRequest: ["payload"], // payload: { params }
  getCampaignSuccess: ["payload"], // payload: { data }
  getCampaignFailure: null,

  createSourceRequest: ["payload"], // payload: { data }
  createSourceSuccess: ["payload"], // payload: { newSource }

  createCampaignRequest: ["payload"], // payload: { data }
  createCampaignSuccess: ["payload"], // payload: { newCampaign }

  generateLinkRequest: ["payload"], // payload: { data }
  generateLinkSuccess: ["payload"], // payload: { newCampaign }

  getSourceDataRequest: ["payload"],
  getSourceDataSuccess: ["payload"],

  getCampaignDataRequest: ["payload"],
  getCampaignDataSuccess: ["payload"],

  getLinkUsersRequest: ["payload"], // payload: { params }
  getLinkUsersSuccess: ["payload"], // payload: { data }
  getLinkUsersFailure: null,

  marketingFailure: ["payload"], // payload: { errors }

  marketingSetProp: ["key", "value"],

  logout: null
})

export const MarketingTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  fetchingSource: false,
  source: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  fetchingCampaign: false,
  campaign: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  options: {
    source: [],
    campaign: []
  },

  fetchingUsers: false,
  users: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  filters: {
    period: "27 days"
  },
  getTotalUsersFetching: false,
  totalUsersData: null,

  saveFetching: false,
  saveResult: null,

  sourceData: null,
  campaignData: null
})

/* ------------- Functions for reducer cases ------------- */

const getMarketingLinksRequest = (state) => state.merge({ fetching: true })
const getMarketingLinksSuccess = (state, { payload: result }) => {
  return state.merge({
    fetching: false,
    result
  })
}

const getSourceDataRequest = (state, { payload }) => state.merge({ fetching: true })
const getSourceDataSuccess = (state, { payload: result }) => {
  return state.merge({ fetching: false, sourceData: result })
}

const getCampaignDataRequest = (state, { payload }) => state.merge({ fetching: true })
const getCampaignDataSuccess = (state, { payload: result }) => {
  return state.merge({ fetching: false, campaignData: result })
}

const getTotalUsersRequest = (state) => state.merge({ getTotalUsersFetching: true })
const getTotalUsersSuccess = (state, { payload: data }) => {
  return state.merge({
    getTotalUsersFetching: false,
    totalUsersData: data
  })
}

const getSourceRequest = (state) => state.merge({ fetchingSource: true })
const getSourceSuccess = (state, { payload: source }) => {
  return state.merge({
    fetchingSource: false,
    source,
    options: {
      ...state.options,
      source: source?.results?.map((item) => ({ value: item.id, label: item.name })) || []
    }
  })
}
const getSourceFailure = (state) => state.merge({ fetchingSource: false })

const getCampaignRequest = (state) => state.merge({ fetchingCampaign: true })
const getCampaignSuccess = (state, { payload: campaign }) => {
  return state.merge({
    fetchingCampaign: false,
    campaign,
    options: {
      ...state.options,
      campaign: campaign?.results?.map((item) => ({ value: item.id, label: item.name })) || []
    }
  })
}
const getCampaignFailure = (state) => state.merge({ fetchingCampaign: false })

const createSourceRequest = (state) => state.merge({ saveFetching: true })
const createSourceSuccess = (state, { payload: newSource }) => {
  return state.merge({
    saveFetching: false,
    saveResult: { type: "source_add", message: "Source has been successfully created." }
  })
}

const createCampaignRequest = (state) => state.merge({ saveFetching: true })
const createCampaignSuccess = (state, { payload: newCampaign }) => {
  return state.merge({
    saveFetching: false,
    saveResult: { type: "campaign_add", message: "Campaign has been successfully created." }
  })
}

const generateLinkRequest = (state) => state.merge({ saveFetching: true })
const generateLinkSuccess = (state, { payload: newLink }) => {
  return state.merge({
    saveFetching: false,
    saveResult: { type: "generate_link", message: "Link has been successfully created." }
  })
}

const getLinkUsersRequest = (state) => state.merge({ fetchingUsers: true })
const getLinkUsersSuccess = (state, { payload: users }) => {
  return state.merge({
    fetchingUsers: false,
    users
  })
}
const getLinkUsersFailure = (state) => state.merge({ fetchingUsers: false })

const marketingFailure = (state) =>
  state.merge({
    fetching: false,
    saveFetching: false,
    getTotalUsersFetching: false
  })

export const marketingSetProp = (state, { key, value }) => {
  const obj = {}
  if (key === "filters") {
    obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state.filters, ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

const logout = () => INITIAL_STATE

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_MARKETING_LINKS_REQUEST]: getMarketingLinksRequest,
  [Types.GET_MARKETING_LINKS_SUCCESS]: getMarketingLinksSuccess,

  [Types.GET_SOURCE_DATA_REQUEST]: getSourceDataRequest,
  [Types.GET_SOURCE_DATA_SUCCESS]: getSourceDataSuccess,

  [Types.GET_CAMPAIGN_DATA_REQUEST]: getCampaignDataRequest,
  [Types.GET_CAMPAIGN_DATA_SUCCESS]: getCampaignDataSuccess,

  [Types.GET_TOTAL_USERS_REQUEST]: getTotalUsersRequest,
  [Types.GET_TOTAL_USERS_SUCCESS]: getTotalUsersSuccess,

  [Types.GET_SOURCE_REQUEST]: getSourceRequest,
  [Types.GET_SOURCE_SUCCESS]: getSourceSuccess,
  [Types.GET_SOURCE_FAILURE]: getSourceFailure,

  [Types.GET_CAMPAIGN_REQUEST]: getCampaignRequest,
  [Types.GET_CAMPAIGN_SUCCESS]: getCampaignSuccess,
  [Types.GET_CAMPAIGN_FAILURE]: getCampaignFailure,

  [Types.CREATE_SOURCE_REQUEST]: createSourceRequest,
  [Types.CREATE_SOURCE_SUCCESS]: createSourceSuccess,

  [Types.CREATE_CAMPAIGN_REQUEST]: createCampaignRequest,
  [Types.CREATE_CAMPAIGN_SUCCESS]: createCampaignSuccess,

  [Types.GENERATE_LINK_REQUEST]: generateLinkRequest,
  [Types.GENERATE_LINK_SUCCESS]: generateLinkSuccess,

  [Types.GET_LINK_USERS_REQUEST]: getLinkUsersRequest,
  [Types.GET_LINK_USERS_SUCCESS]: getLinkUsersSuccess,
  [Types.GET_LINK_USERS_FAILURE]: getLinkUsersFailure,

  [Types.MARKETING_FAILURE]: marketingFailure,

  [Types.MARKETING_SET_PROP]: marketingSetProp,

  [Types.LOGOUT]: logout
})
